import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';

import './EditCodingForm.scss';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import {
  useDeleteCodingStamp,
  useGetEditCodingDetails,
  useGetEditCodingStamp,
  useSaveEditCodingStamp,
} from '../../../../hooks/document/useDocument';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ReactComponent as TagTreeIcon} from '../../../../assets/images/tag-tree.svg';
import {ReactComponent as DeleteIcon} from '../../../../assets/images/delete.svg';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {t} from 'i18next';
import ArrButton from '../../../common/arr-button/ArrButton';
import {CodingFormItem} from '../../../../shared/types';
import DynamicFormFields from '../../../coding-forms-management/create-edit-coding-form/preview-coding-forms/DynamicFormFields';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {ToastType} from '../../../../shared/constants';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';

interface EditCodingFormProps {
  showPanel: boolean;
  setShowPanel: React.Dispatch<React.SetStateAction<boolean>>;
  defaultCode: string;
  defaultCodingStampName: string;
  onSuccess?: () => void;
  codingFormId?: number | string | undefined;
  selectedCodingFormName?: string;
  onCloseDrawer?: () => void;
}
interface CodingStamp {
  codingFormUserStampID: number;
  codingFormStampColorCode: string;
  codingFormStampColorName: string;
  codingFormStampColorLabel: string;
  codingFormStampColorRGB: string;
  isDisabled: boolean;
}
const EditCodingForm: React.FC<EditCodingFormProps> = ({
  showPanel,
  setShowPanel,
  defaultCode,
  defaultCodingStampName,
  codingFormId,
  selectedCodingFormName,
  onSuccess,
  onCloseDrawer,
}) => {
  const [selectedStamp, setSelectedStamp] = useState<any>(null);
  const [defaultSelectedStampId, setDefaultSelectedStampId] =
    useState<any>(null);
  const [selectedStampIndex, setSelectedStampIndex] = useState<number | null>(
    null
  );
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [stampList, setStampList] = useState<any[]>([]);
  const [documentCodingForm, setDocumentCodingForm] = useState<
    CodingFormItem[]
  >([]);
  const [codingStampName, setCodingStampName] = useState<string>('');
  const [isConfirmMode, setIsConfirmMode] = useState<boolean>(false);
  const [confirmType, setConfirmType] = useState<'save' | 'delete' | null>(
    null
  );
  const [initialState, setInitialState] = useState<{
    selectedStamp: any;
    codingStampName: string;
  }>({
    selectedStamp: null,
    codingStampName: '',
  });

  const {
    mutate: getEditCodingStamp,
    data: codingStampData = [] as CodingStamp[],
  } = useGetEditCodingStamp();
  const {mutate: getEditCodingDetails, data: codingStampDetails} =
    useGetEditCodingDetails();
  const {mutate: mutateSaveEditCodingStamp} = useSaveEditCodingStamp();
  const {mutate: mutateDeleteCodingStamp} = useDeleteCodingStamp();
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );
  const docView = useSelector((state: RootState) => state.docView);
  const selectedSourceId = useSelector(
    (state: RootState) => state.docView.sourceId
  );
  const activeDocumentId = Number(activeDocumentObj?.documentId);
  const onClose = () => {
    setShowPanel(false);
    setIsConfirmMode(false);
    setConfirmType(null);
    onCloseDrawer?.();
  };

  useEffect(() => {
    if (selectedStamp) {
      getEditCodingDetails({
        sourceID: selectedSourceId ?? 0,
        CodingFormId: Number(codingFormId),
        DocumentId: activeDocumentId,
        Code: selectedStamp.codingFormStampColorCode,
        SourceTypeCode: 'DOCUMENT',
      });
    }
  }, [selectedStamp, codingFormId]);

  useEffect(() => {
    getEditCodingStamp({
      sourceID: selectedSourceId ?? 0,
      userCodingFormID: Number(codingFormId),
    });
  }, []);

  // Initialize selectedStamp with defaultCode on mount or when codingStampData changes
  useEffect(() => {
    if (codingStampData && codingStampData.length > 0) {
      setStampList(codingStampData);
      const defaultIndex = codingStampData.findIndex(
        (stamp: any) => stamp.codingFormStampColorCode === defaultCode
      );
      if (defaultIndex !== -1) {
        const defaultStamp = codingStampData[defaultIndex];
        setSelectedStamp(defaultStamp);
        setSelectedStampIndex(defaultIndex);
        setDefaultSelectedStampId(defaultStamp?.codingFormUserStampID);
        const stampName =
          defaultStamp.codingFormStampColorName ||
          defaultStamp.codingFormStampColorLabel ||
          '';
        setCodingStampName(stampName);
        // Set initial state after loading data
        setInitialState({
          selectedStamp: defaultStamp,
          codingStampName: stampName,
        });
      } else if (codingStampData.length > 0) {
        const firstStamp = codingStampData[0];
        if (firstStamp) {
          setSelectedStamp(firstStamp);
          setSelectedStampIndex(0);
          const stampName =
            firstStamp.codingFormStampColorName ||
            firstStamp.codingFormStampColorLabel ||
            '';
          setCodingStampName(stampName);

          // Set initial state after loading data
          setInitialState({
            selectedStamp: firstStamp,
            codingStampName: stampName,
          });
        }
      }
    }
  }, [codingStampData, defaultCode]);

  useEffect(() => {
    if (codingStampDetails) {
      const newItems = codingStampDetails?.map(
        (control: any, index: number) => {
          let itemId = Number(control.contextID);
          let value = '';
          if (control.systemControlName) {
            const parts = control.systemControlName.split('_');
            if (parts.length > 1) {
              const parsedId = Number(parts[1]);
              if (!isNaN(parsedId)) {
                itemId = parsedId;
                value = parts[1];
              }
            }
          }
          return {
            id: control.id,
            cfItemID: index,
            sequence: control.sequence,
            type: control.contextType || '',
            specialFlag: control.isSpecial || false,
            value: value || String(itemId),
            systemLabel: control.systemControlName || '',
            friendlyLabel: control.customControlLabel || '',
            logicRuleCount: String(control.logicRuleCount || '0'),
            tagType: control.codingFormControlTypeCode || '',
            errorMessage: control.errorMessage || '',
            defaultAction: control.defaultAction || '',
            helpText: control.helpText || '',
            fieldLogic: JSON.parse(control.controlLogicJSon || '[]'),
            fieldLogicJson: control.controlLogicJSon || '',
            fieldLogicAction: control.fieldLogicAction || '',
            fieldLogicObjectLabel: `${control.customControlLabel} (${control.systemControlName})`,
            groupAssociation: control.groupControlName
              ? {
                  value: control.groupControlName,
                  label: control.groupControlName,
                }
              : null,
            metaDataType: control.metaDataType || '',
            maxLength: control.maxLength
              ? Number(control.maxLength)
              : undefined,
            minValue: control.minValue || '',
            maxValue: control.maxValue || '',
            splitLength: control.splitLength
              ? Number(control.splitLength)
              : undefined,
            dataTypeLengthCode: control.dataTypeLengthCode || '',
            isCollapsed: true,
            controlValue: control.controlValue,
            displayType: control.groupDisplayType || 'DEFAULT',
          };
        }
      );
      setDocumentCodingForm(newItems);
    }
  }, [codingStampDetails]);

  // Check if there are any changes
  const hasChanges = () => {
    const stampChanged =
      selectedStamp?.codingFormUserStampID !==
      initialState.selectedStamp?.codingFormUserStampID;
    const nameChanged =
      codingStampName.trim() !== initialState.codingStampName.trim();

    return stampChanged || nameChanged;
  };

  const handleSelectChange = (value: any) => {
    // value is the selected item from dropdown, contains key in format 'stamp-{index}'
    if (value && value.key) {
      const indexStr = value.key.split('-')[1];
      const index = parseInt(indexStr, 10);
      const stamp = (codingStampData || stampList)[index];
      if (stamp) {
        setSelectedStamp(stamp);
        setSelectedStampIndex(index);
        setCodingStampName(
          stamp.codingFormStampColorName ||
            stamp.codingFormStampColorLabel ||
            ''
        );
      }
    }
  };
  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const {value, name} = event.target;

    if (name === 'codingStampName') {
      setCodingStampName(value);
    }
  };

  // Initialize input from parent-provided default name (if any)
  useEffect(() => {
    if (defaultCodingStampName) {
      setCodingStampName(defaultCodingStampName);
    }
  }, [defaultCodingStampName]);

  // Keep input synced when selectedStamp is set programmatically
  useEffect(() => {
    if (selectedStamp) {
      setCodingStampName(selectedStamp.codingFormStampColorName || '');
    }
  }, [selectedStamp]);
  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
      if (status === ToastType.Success) {
        onSuccess?.();
        onClose();
      }
    }, 1000);
  };

  const handleDeleteCodingStamp = () => {
    const saveEditStampPayload = {
      sessionObject: {
        documentListMasterID: docView.documentListMasterId,
        sourceTypeCode: docView?.sourceTypeCode || 'DOCEXPLORER',
        docsForCodeSame: [''],
        assignmentSource: '',
        sourceID: 0,
        userCodingFormID: Number(codingFormId) || 1,
        xmlCompletedDocumentEditedCodingForm: '',
      },
      colorCode: selectedStamp?.codingFormStampColorLabel,
      codingFormStampId: selectedStamp?.codingFormUserStampID,
      isDeleted: 1,
    };
    mutateDeleteCodingStamp(saveEditStampPayload, {
      onSuccess: (data) => {
        triggerToast(
          data.message ||
            t('docView.headers.codingForm.codingStampDeleteSuccess'),
          ToastType.Success
        );
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  };
  const handleSaveCodingStamp = () => {
    const saveEditStampPayload = {
      sessionObject: {
        documentListMasterID: docView.documentListMasterId,
        sourceTypeCode: docView?.sourceTypeCode || 'DOCEXPLORER',
        docsForCodeSame: [''],
        assignmentSource: '',
        sourceID: selectedSourceId ?? 0,
        userCodingFormID: Number(codingFormId),
        xmlCompletedDocumentEditedCodingForm: '',
      },
      colorCode: selectedStamp?.codingFormStampColorLabel,
      codingFormStampId: defaultSelectedStampId,
      isDeleted: 0,
      codingStampName: codingStampName,
    };
    mutateSaveEditCodingStamp(saveEditStampPayload, {
      onSuccess: (data) => {
        triggerToast(
          data.message ||
            t('docView.headers.codingForm.codingStampSavedSucces'),
          ToastType.Success
        );
        // Update initial state after successful save
        setInitialState({
          selectedStamp,
          codingStampName,
        });
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  };
  const openConfirm = (type: 'save' | 'delete') => {
    setConfirmType(type);
    setIsConfirmMode(true);
  };

  const handleConfirmNo = () => {
    setIsConfirmMode(false);
    setConfirmType(null);
  };

  const handleConfirmYes = () => {
    if (confirmType === 'save') {
      handleSaveCodingStamp();
    } else if (confirmType === 'delete') {
      handleDeleteCodingStamp();
    }
    setIsConfirmMode(false);
    setConfirmType(null);
  };

  const DrawerHeader = () => (
    <Box className="edit-coding-form-header">
      <Box className="title-box">
        <Typography
          className="typo-text headline3_size title"
          data-testid="edit-coding-stamp-title"
        >
          {t('docView.headers.codingForm.editCodingStamp')}
        </Typography>
      </Box>
      {!isConfirmMode ? (
        <Box className="action-buttons-container">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={() => {
              onClose();
            }}
            disabled={false}
            dataTestId="edit-coding-form-cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="medium"
            onClick={
              !selectedStamp?.isDisabled
                ? handleSaveCodingStamp
                : () => openConfirm('save')
            }
            disabled={
              !codingStampName?.trim() || !selectedStamp || !hasChanges()
            }
            dataTestId="edit-coding-stamp-save-btn"
          >
            {t('common.save')}
          </ArrButton>
        </Box>
      ) : null}
    </Box>
  );
  const renderDropdownAndTextBox = () => (
    <>
      <Box className="row1">
        <Box className="edit-coding-form-dropdown">
          <ArrSingleSelectDropdown
            dropdownLabel={'Select Stamp'}
            dropdownItems={(codingStampData || stampList)?.map(
              (stamp, index) => ({
                ...stamp,
                key: `stamp-${index}`,
                value: stamp.isDisabled ? 'Assigned' : 'New Stamp',
                label: stamp.isDisabled ? 'Assigned' : 'New Stamp',
                id: stamp.codingFormStampColorRGB,
                name: stamp.codingFormStampColorCode,
              })
            )}
            selectedValues={
              selectedStamp && selectedStampIndex !== null
                ? {
                    ...selectedStamp,
                    key: `stamp-${selectedStampIndex}`,
                    value: selectedStamp.isDisabled ? 'Assigned' : 'New Stamp',
                    label: selectedStamp.isDisabled ? 'Assigned' : 'New Stamp',
                    id: selectedStamp.codingFormStampColorRGB,
                    name: selectedStamp.codingFormStampColorCode,
                  }
                : null
            }
            dropdownItemKey={'key'}
            dropdownItemDisplay={'value'}
            formControlRestProps={{}}
            onSelectionChange={handleSelectChange}
            dataTestId="asd-edit-coding-form-selected-stamp"
            renderPrefix={(item: any) => (
              <ArrIconButton
                key={item.codingFormStampColorLabel}
                shape="none"
                size="small"
                icon={<TagTreeIcon style={{color: `#${item.id}`}} />}
                ariaLabel="edit-stamp"
                tooltipContent={item?.codingFormStampColorName}
                testId={`edit-stamp-${item.codingFormStampColorLabel}`}
              />
            )}
            renderSelectedPrefix={(item: any) => (
              <ArrIconButton
                key={item?.codingFormStampColorLabel}
                shape="none"
                size="small"
                icon={<TagTreeIcon style={{color: `#${item?.id}`}} />}
                ariaLabel="edit-stamp"
                tooltipContent={item?.codingFormStampColorName}
                testId="edit-stamp-selected"
              />
            )}
          />
        </Box>
        <ArrButton
          variant="secondary"
          size="medium"
          aria-label={t('common.delete')}
          dataTestId="delete-button"
          startIcon={<DeleteIcon />}
          disabled={!selectedStamp?.isDisabled}
          className="edit-coding-form-delete-btn"
          onClick={() => openConfirm('delete')}
        >
          {t('docView.headers.codingForm.deleteStamp')}
        </ArrButton>
      </Box>
      <Box className="edit-coding-form-textfield">
        <ArrTextField
          id="coding-stamp-name-input"
          label={t('docView.headers.codingForm.codingStampName')}
          name="codingStampName"
          aria-label={t('docView.headers.codingForm.codingStampName')}
          value={codingStampName}
          onChange={handleTextInputChange}
          dataTestId="edit-coding-first-name"
        />
      </Box>
    </>
  );

  const renderConfirmationContent = () => (
    <Box className="edit-coding-form-confirmation">
      <Box className="confirmation-content">
        <ArrIconButton
          key="confirmation"
          shape="none"
          size="small"
          icon={
            <TagTreeIcon
              style={{color: `#${selectedStamp?.codingFormStampColorRGB}`}}
            />
          }
          ariaLabel="confirmation-stamp"
          tooltipContent=""
          testId="confirmation-stamp"
        />
        <Typography
          className="typo-text body2_size confirmation-text"
          data-testid="confirmation-text"
        >
          {t('docView.headers.codingForm.inUse')} <br data-testid={`edit-coding-form-br`} />
          {t('docView.headers.codingForm.questionGeneric', {
            action: t(
              confirmType === 'save'
                ? t('docView.headers.codingForm.overwrite').toLowerCase()
                : t('common.delete').toLowerCase()
            ),
          })}
        </Typography>
      </Box>
      <Box className="confirmation-buttons">
        <ArrButton
          variant={confirmType === 'save' ? 'primary' : 'danger'}
          size="medium"
          onClick={handleConfirmYes}
          dataTestId="confirm-yes"
        >
          {t('docView.headers.codingForm.yesWithAction', {
            action: t(
              confirmType === 'save'
                ? t('docView.headers.codingForm.overwrite')
                : t('common.delete')
            ),
          })}
        </ArrButton>
        <ArrButton
          variant="secondary"
          size="medium"
          onClick={handleConfirmNo}
          dataTestId="confirm-no"
        >
          {t('common.no')}
        </ArrButton>
      </Box>
    </Box>
  );

  const drawerContents = (
    <>
      <Box className="edit-coding-form-Container">
        <DrawerHeader />
        <Box className="edit-coding-form-content">
          {!isConfirmMode
            ? renderDropdownAndTextBox()
            : renderConfirmationContent()}
          {!isConfirmMode ? (
            <Box className="dynamic-form-fields-container">
              <Box className="section-title-box">
                <Typography
                  className="typo-text headline5_size"
                  data-testid="coding-form-fields-title"
                >
                  {selectedCodingFormName}
                </Typography>
              </Box>
              <Box className="dynamic-form-fields-box">
                <DynamicFormFields
                  fields={documentCodingForm}
                  setMessageType={() => {}}
                  showTestButton={false}
                  disableAll={true}
                />
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </>
  );

  return (
    <Box>
      <ArrDrawer
        isOpen={showPanel}
        showHeader={false}
        showButton={false}
        ariaLabel="drawer content"
        size="small"
        side="right"
        onClose={() => onClose()}
      >
        {drawerContents}
      </ArrDrawer>
    </Box>
  );
};

export default EditCodingForm;
